import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tag, Spin,
  Tooltip, Avatar, Empty, Alert
} from 'antd';
import {
  SearchOutlined, UserOutlined, LogoutOutlined,
  ReloadOutlined, ArrowLeftOutlined, InboxOutlined
} from '@ant-design/icons';
import AgentConfigService from '../services/AgentConfigService';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// ── Service URL config ────────────────────────────────────────────────────────
// Maps workType URL param → backend endpoint path on work_queue_service
const WORK_TYPE_ENDPOINTS = {
  charts:  '/get-work-items/charts',
  appeals: '/get-work-items/appeals',
  audits:  '/get-work-items/audits',
};

// ── Status pill config ────────────────────────────────────────────────────────
const STATUS_STYLE = {
  ASSIGNED:    { bg: '#DCFCE7', color: '#15803D', border: '#86EFAC', label: 'Ready'       },
  IN_REVIEW:   { bg: '#FEF9C3', color: '#92400E', border: '#FDE68A', label: 'In Progress' },
  IN_PROGRESS: { bg: '#FEF9C3', color: '#92400E', border: '#FDE68A', label: 'In Progress' },
  CODED:       { bg: '#DBEAFE', color: '#1E40AF', border: '#BFDBFE', label: 'Completed'   },
  COMPLETED:   { bg: '#DBEAFE', color: '#1E40AF', border: '#BFDBFE', label: 'Completed'   },
  NOT_READY:   { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB', label: 'Not Ready'   },
};

// ── Action button style ───────────────────────────────────────────────────────
const ACTION_STYLE = {
  primary: { background: '#162349', color: '#fff',     border: '#162349' },
  default: { background: '#92A372', color: '#fff',     border: '#92A372' },
  ghost:   { background: 'white',   color: '#162349',  border: '#162349' },
};

// ── Table layout constants ───────────────────────────────────────────────────
// Shared by TableHeader and QueueRow so columns stay aligned, and reused by
// computeTableMinWidth() to size the horizontal-scroll viewport. Keep these
// in sync with the `padding` and `gap` props on the row / header flex
// containers below.
const STATUS_COL_WIDTH = 130;
const ACTION_COL_WIDTH = 140;
const ROW_HPADDING     = 40;   // 20px left + 20px right (row & header padding)
const COL_GAP          = 8;    // `gap` on row & header flex containers

// Minimum width needed to render every column at its configured width.
// Below this, the table region shows a horizontal scrollbar; above it the
// table is visually identical to before this fix.
const computeTableMinWidth = (columns) => {
  const dataWidth = columns.reduce((sum, c) => sum + (c.width || 100), 0);
  const numCells  = columns.length + 2;                 // + status + action
  const gapsWidth = Math.max(0, numCells - 1) * COL_GAP;
  return dataWidth + STATUS_COL_WIDTH + ACTION_COL_WIDTH + gapsWidth + ROW_HPADDING;
};

// ── Tag color map — matches tag_color values from API column config ─────────────
const TAG_COLOR = {
  default: { bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' },
  blue:    { bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
  purple:  { bg: '#EDE9FE', color: '#5B21B6', border: '#C4B5FD' },
  green:   { bg: '#DCFCE7', color: '#15803D', border: '#86EFAC' },
};

// ── Tag pill — used inside list-type cells ────────────────────────────────────
const TagPill = ({ label, colorKey = 'default' }) => {
  const c = TAG_COLOR[colorKey] || TAG_COLOR.default;
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
      marginRight: 3,
      marginBottom: 2,
    }}>
      {label}
    </span>
  );
};

// ── Status pill ───────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.NOT_READY;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 0',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.03em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap',
      minWidth: 96,
      textAlign: 'center',
    }}>
      {s.label}
    </span>
  );
};

// ── Table row ─────────────────────────────────────────────────────────────────
const QueueRow = ({ item, columns, onAction, isEven }) => {
  const as     = item.action_label ? ACTION_STYLE[item.action_variant] || ACTION_STYLE.default : null;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: '1px solid #E5E7EB',
        background: hovered ? '#F0F4FF' : isEven ? '#FAFAFA' : '#fff',
        transition: 'background 0.12s',
        gap: 8,
      }}
    >
      {/* Data columns */}
      {columns.map(col => {
        const val      = item[col.key];
        const isTags   = col.type === 'tags';
        const isFirst  = col.key === columns[0].key;
        const isEmpty  = !val || (Array.isArray(val) && val.length === 0);

        return (
          <div
            key={col.key}
            style={{
              width: col.width,
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              flexWrap: isTags ? 'wrap' : 'nowrap',
            }}
          >
            {isTags ? (
              isEmpty ? (
                <span style={{ fontSize: 12, color: '#D1D5DB' }}>—</span>
              ) : (
                (Array.isArray(val) ? val : [val]).map((tag, i) => (
                  <TagPill key={i} label={tag} colorKey={col.tag_color || 'default'} />
                ))
              )
            ) : (
              <span style={{
                fontSize: 15,
                color: isFirst ? '#111827' : '#374151',
                fontWeight: isFirst ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: isFirst ? 'monospace' : 'inherit',
              }}>
                {val ?? '—'}
              </span>
            )}
          </div>
        );
      })}

      {/* Status */}
      <div style={{ width: STATUS_COL_WIDTH, flexShrink: 0 }}>
        <StatusPill status={item.status} />
      </div>

      {/* Action — fixed width (was flex:1) so it can't be squeezed off-screen
          on narrow viewports; the wrapping overflow-x:auto container lets the
          user scroll right to reach it instead. */}
      <div style={{ width: ACTION_COL_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
        {as ? (
          <button
            onClick={() => onAction(item)}
            style={{
              padding: '5px 0',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              background: as.background,
              color: as.color,
              border: `1px solid ${as.border}`,
              transition: 'opacity 0.15s',
              minWidth: 100,
              textAlign: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {item.action_label}
          </button>
        ) : (
          <span style={{ fontSize: 14, color: '#9CA3AF' }}>—</span>
        )}
      </div>
    </div>
  );
};

// ── Table header row ──────────────────────────────────────────────────────────
const TableHeader = ({ columns }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    background: '#162349',
    borderRadius: '6px 6px 0 0',
    gap: 8,
  }}>
    {columns.map(col => (
      <div
        key={col.key}
        style={{
          width: col.width,
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 700,
          color: '#BDD8E5',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {col.label}
      </div>
    ))}
    <div style={{ width: STATUS_COL_WIDTH, flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#BDD8E5', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      Status
    </div>
    <div style={{ width: ACTION_COL_WIDTH, flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#BDD8E5', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'right' }}>
      Action
    </div>
  </div>
);

// ── Status filter tabs ────────────────────────────────────────────────────────
const STATUS_FILTERS = ['ALL', 'ASSIGNED', 'IN_REVIEW', 'CODED', 'NOT_READY'];

const FilterTab = ({ label, active, count, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '5px 14px',
      borderRadius: 9999,
      fontSize: 14,
      fontWeight: active ? 700 : 400,
      cursor: 'pointer',
      background: active ? '#162349' : 'white',
      color: active ? 'white' : '#6B7280',
      border: `1px solid ${active ? '#162349' : '#E5E7EB'}`,
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      gap: 5,
    }}
  >
    {label === 'ALL' ? 'All' : STATUS_STYLE[label]?.label || label}
    {count != null && (
      <span style={{
        background: active ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
        color: active ? 'white' : '#374151',
        borderRadius: 9999,
        padding: '0 6px',
        fontSize: 13,
        fontWeight: 700,
      }}>
        {count}
      </span>
    )}
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────
const WorkQueuePage = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const { workType } = useParams();
  const navigate     = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [error,         setError]         = useState(null);
  const [queueData,     setQueueData]     = useState(null);   // full API response
  const [searchText,    setSearchText]    = useState('');
  const [activeFilter,  setActiveFilter]  = useState('ALL');
  const [actionLoading, setActionLoading] = useState(null);   // item_id being acted on

  // ── Fetch queue data ───────────────────────────────────────────────────────
  const fetchQueue = useCallback(async (isManual = false) => {
    if (!workType || !WORK_TYPE_ENDPOINTS[workType]) {
      setError(`Unknown work type: "${workType}". Expected one of: ${Object.keys(WORK_TYPE_ENDPOINTS).join(', ')}`);
      setLoading(false);
      return;
    }

    if (isManual) { setRefreshing(true); } else { setLoading(true); }
    setError(null);

    try {
      const baseUrl    = await AgentConfigService.getWorkQueueServiceUrl();
      const endpoint   = WORK_TYPE_ENDPOINTS[workType];
      const response   = await fetch(`${baseUrl}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setQueueData(data);
    } catch (err) {
      console.error('WorkQueuePage fetch error:', err);
      setError(err.message || 'Failed to load inventory. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [workType, sessionId]);

  useEffect(() => { fetchQueue(false); }, [fetchQueue]);

  // ── Handle action click (Start / Continue / View) ──────────────────────────
  const handleAction = async (item) => {
    const baseUrl      = await AgentConfigService.getWorkQueueServiceUrl();
    const actionUrl    = `${queueData.action_base_url}/${item.id}`;
    setActionLoading(item.id);

    try {
      // If ASSIGNED or READY → mark in_review before navigating
      if (item.status === 'ASSIGNED' || item.status === 'READY') {
        await fetch(`${baseUrl}/work-items/${item.id}/start`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include',
        });
      }
      navigate(item.batch_id ? `${actionUrl}?batch_id=${encodeURIComponent(item.batch_id)}` : actionUrl);

    } catch (err) {
      console.error('Action error:', err);
      // Navigate anyway — the review page will handle state
      navigate(item.batch_id ? `${actionUrl}?batch_id=${encodeURIComponent(item.batch_id)}` : actionUrl);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Derived: filter + search ───────────────────────────────────────────────
  const allItems = queueData?.items || [];

  const filteredItems = allItems.filter(item => {
    const matchesFilter = activeFilter === 'ALL' || item.status === activeFilter;
    if (!matchesFilter) return false;
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return Object.values(item).some(v =>
      v != null && String(v).toLowerCase().includes(q)
    );
  });

  // Count per status for filter tabs
  const statusCounts = allItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});

  const columns = queueData?.columns || [];
  const title   = queueData?.title   || 'My Inventory';

  // ── Breadcrumb back label ──────────────────────────────────────────────────
  const backLabel = workType === 'charts' ? 'HCC Workspace' : 'Workspace';
  const backRoute = workType === 'charts' ? '/hcc-home'     : '/agent-hub';

  return (
    <Layout style={{ minHeight: '100vh' }}>

      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          alignItems: 'center',
          padding: '20px 0'
        }}>
          <div></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{
                  backgroundColor: 'white',
                  color: '#162349',
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />}
              />
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: "Bold" }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header — AgentPageV3 pattern ── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate(backRoute)}
              style={{ color: 'rgba(255,255,255,0.75)', fontSize: 20, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'white'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
            >
              {backLabel.toUpperCase()}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{ color: 'white', fontSize: 20, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title || 'MY INVENTORY'}
            </span>
          </div>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={() => fetchQueue(true)}
            style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
          >
            Refresh
          </Button>
        </Header>

        {/* ── Content ── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {/* Error state */}
          {error && !loading && (
            <Alert
              type="error"
              message="Failed to load inventory"
              description={error}
              showIcon
              closable
              onClose={() => setError(null)}
              action={
                <Button size="small" onClick={fetchQueue} icon={<ReloadOutlined />}>
                  Retry
                </Button>
              }
              style={{ marginBottom: 20 }}
            />
          )}

          {/* Loading state */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading inventory…
              </div>
            </div>
          )}

          {/* Main content card */}
          {!loading && !error && queueData && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}>

              {/* Search + filter bar */}
              <div style={{
                padding: '14px 20px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                background: '#FAFAFA',
              }}>

                {/* Status filter tabs */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {STATUS_FILTERS.map(f => (
                    <FilterTab
                      key={f}
                      label={f}
                      active={activeFilter === f}
                      count={f === 'ALL' ? allItems.length : statusCounts[f]}
                      onClick={() => setActiveFilter(f)}
                    />
                  ))}
                </div>

                {/* Search */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                    placeholder="Search chart…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 220, borderRadius: 4 }}
                  />
                </div>
              </div>

              {/* Horizontally scrollable table region.
                  Without this wrapper the sum of configured column widths
                  exceeds typical laptop viewport widths, and the Status /
                  Action columns get clipped because the outer card uses
                  overflow:hidden. overflowX:auto on the outer div + a
                  computed minWidth on the inner one gives a horizontal
                  scrollbar exactly when needed and leaves the layout
                  untouched on wide screens. Search bar and footer stay
                  outside this wrapper so they remain full-width. */}
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: computeTableMinWidth(columns) }}>
                  {/* Table header */}
                  {columns.length > 0 && <TableHeader columns={columns} />}

                  {/* Table body */}
                  {filteredItems.length === 0 ? (
                    <div style={{ padding: '60px 0', textAlign: 'center' }}>
                      <Empty
                        image={<InboxOutlined style={{ fontSize: 48, color: '#D1D5DB' }} />}
                        imageStyle={{ height: 60 }}
                        description={
                          <Text style={{ color: '#9CA3AF', fontSize: 14 }}>
                            {searchText
                              ? `No items match "${searchText}"`
                              : activeFilter !== 'ALL'
                                ? `No ${STATUS_STYLE[activeFilter]?.label || activeFilter} items`
                                : 'No items in your queue'
                            }
                          </Text>
                        }
                      />
                    </div>
                  ) : (
                    filteredItems.map((item, idx) => (
                      <QueueRow
                        key={item.id}
                        item={actionLoading === item.id
                          ? { ...item, action_label: '…' }
                          : item
                        }
                        columns={columns}
                        onAction={handleAction}
                        isEven={idx % 2 === 0}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Footer — item count */}
              <div style={{
                padding: '10px 20px',
                borderTop: '1px solid #E5E7EB',
                background: '#FAFAFA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <Text style={{ fontSize: 12, color: '#9CA3AF' }}>
                  Showing {filteredItems.length} of {allItems.length} items
                </Text>
                <Text style={{ fontSize: 12, color: '#9CA3AF' }}>
                  {username} · {userRole}
                </Text>
              </div>

            </div>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

export default WorkQueuePage;